import { ReactNode } from 'react';

import { createPath } from 'react-router-dom';

import {
  Button,
  ScrollableDialogContent,
  Spinner,
  DialogFooter,
  ExternalLink,
  Typography,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { useLocationWithOrgId } from 'hooks/useLocationWithOrgId';

import { BackButton } from '../BackButton';
import { ClusterDialogTitle } from '../ClusterDialogTitle';
import { StepContainer } from '../StepLayout';

const DIALOG_CONTENT_PADDING = 24;
const CONTENT_LEFT_PADDING = 46;

type Props = {
  children: ReactNode;
  title: ReactNode;
  onBack: () => void;
  onClose: () => void;
  testId?: string;
};

export const EnablingStepLayout = ({
  children,
  title,
  onBack,
  onClose,
  testId = 'connecting-progress-step',
}: Props) => {
  const componentControlLocation = useLocationWithOrgId(
    '/organization/management/component-control/components'
  );
  const componentControlLink =
    typeof componentControlLocation === 'string'
      ? componentControlLocation
      : createPath(componentControlLocation ?? {});

  return (
    <StepContainer position="relative" data-testid={testId}>
      <ScrollableDialogContent sx={{ p: DIALOG_CONTENT_PADDING, pb: 0 }}>
        <Stack gap={8}>
          <Stack direction="row" gap={12} alignItems="center">
            <Spinner size={32} color="secondary" />
            <ClusterDialogTitle>{title}</ClusterDialogTitle>
          </Stack>

          <Stack pl={CONTENT_LEFT_PADDING}>{children}</Stack>

          <Stack
            py={8}
            px={DIALOG_CONTENT_PADDING}
            pl={DIALOG_CONTENT_PADDING + CONTENT_LEFT_PADDING + 2}
            mx={-DIALOG_CONTENT_PADDING}
            gap={4}
            bgcolor={colorOrToken('blue.50', tokens.theme.bg.status.infoSubtle)}
            borderTop={colorOrToken(
              'blue.100',
              tokens.theme.border.status.infoSubtle
            )}
            borderBottom={colorOrToken(
              'blue.100',
              tokens.theme.border.status.infoSubtle
            )}
          >
            <Typography variant="P12R" color="textSecondary">
              You can monitor installed components in{' '}
              <ExternalLink link={componentControlLink}>
                Component control page
              </ExternalLink>
            </Typography>
            <Typography variant="P12R" color="textSecondary">
              Closing this dialog will not affect the process.
            </Typography>
          </Stack>
        </Stack>
      </ScrollableDialogContent>

      <DialogFooter
        sx={{ borderTop: 'none' }}
        start={<BackButton onClick={onBack} />}
        end={
          <Button onClick={onClose} testId="close-button">
            Close
          </Button>
        }
      />
    </StepContainer>
  );
};
